import React, { useEffect, useMemo, useState } from 'react';
import { CircularProgress } from '@material-ui/core';
import GradientButton from '@shared/components/GradientButton';
import StyledInput from '@shared/components/StyledInput';
import StyledSelect from '@shared/components/StyledSelect';
import StyledSearchSelect from '@shared/components/StyledSearchSelect';
import dayjs from 'dayjs';

const EventForm = ({
	initialForm,
	types,
	rooms,
	submitLabel = 'Save',
	onSubmit,
	onCancel,
	getStudentsPage,
	isSubmitting = false,
}) => {
	const [form, setForm] = useState(initialForm);
	const [errTxts, setErrTxts] = useState({});
	const [students, setStudents] = useState([]);
	const [studentsLoading, setStudentsLoading] = useState(false);

	const normalizedRooms = useMemo(() => (rooms || []).map((r) => ({ ...r, id: Number(r.id) })), [rooms]);

	const isAllSelected = normalizedRooms.length > 0 && (form.room_id?.length || 0) === normalizedRooms.length;

	const isIndeterminate = (form.room_id?.length || 0) > 0 && (form.room_id?.length || 0) < normalizedRooms.length;

	const selectedRoomId = useMemo(() => {
		if (Array.isArray(form?.room_id) && form.room_id.length === 1) {
			const r = form.room_id[0];
			return r?.id != null ? Number(r.id) : null;
		}
		return null;
	}, [form?.room_id]);

	useEffect(() => setForm(initialForm), [initialForm]);

	useEffect(() => {
		if (!form?.room_id || form.room_id.length !== 1) {
			setForm((prev) => (prev?.student_id ? { ...prev, student_id: null } : prev));
		}
	}, [form?.room_id]);

	// Load all pages for a single selected room
	useEffect(() => {
		let cancelled = false;

		if (!selectedRoomId) {
			setStudents([]);
			return () => {
				cancelled = true;
			};
		}

		(async () => {
			const all = [];
			let page = 1;
			setStudentsLoading(true);
			// eslint-disable-next-line no-constant-condition
			while (true) {
				const res = await getStudentsPage(page, selectedRoomId);
				if (cancelled) return;

				const d = res?.data ?? {};
				const list = Array.isArray(d.data) ? d.data : [];
				all.push(...list);

				if (!d.next_page_url) break;
				page += 1;
			}

			if (!cancelled) setStudents(all);
			setStudentsLoading(false);
		})();

		return () => {
			setStudentsLoading(false);
			cancelled = true;
		};
	}, [selectedRoomId]);

	const handleChange = (e) => {
		let name, value;
		if (e?.target) ({ name, value } = e.target);
		else if (e && typeof e === 'object' && 'name' in e) ({ name, value } = e);
		else {
			name = 'date';
			value = e;
		}

		setErrTxts((prev) => ({ ...prev, [name]: '' }));

		if (name === 'date') {
			const d = dayjs(value);
			setForm((prev) => ({ ...prev, date: d.isValid() ? d : null }));
			return;
		}

		if (name === 'room_id') {
			const values = Array.isArray(value) ? value : [];
			const selected = values
				.filter((v) => v?.value !== 'all')
				.map((v) => normalizedRooms.find((r) => Number(r.id) === Number(v.value)))
				.filter(Boolean);

			setForm((prev) => ({ ...prev, room_id: selected }));
			return;
		}

		setForm((prev) => ({ ...prev, [name]: value }));
	};

	const handleSelectAll = () => {
		const next = normalizedRooms.length === (form.room_id?.length || 0) ? [] : normalizedRooms;
		setForm((prev) => ({ ...prev, room_id: next }));
	};

	const handleSubmit = (ev) => {
		ev.preventDefault();
		setErrTxts({});

		if (!form.date) {
			setErrTxts((prev) => ({ ...prev, date: 'This field is required' }));
			return;
		}
		if (!form.type) {
			setErrTxts((prev) => ({ ...prev, type: 'This field is required' }));
			return;
		}
		if (!form.room_id || form.room_id.length === 0) {
			setErrTxts((prev) => ({ ...prev, room_id: 'This field is required' }));
			return;
		}
		if (form?.description?.length > 500) {
			setErrTxts((prev) => ({
				...prev,
				description: 'The description may not be greater than 500 characters.',
			}));
			return;
		}

		const selectedType = types.find((t) => t.id === form.type);
		const isCustomType = selectedType?.type?.toLowerCase() === 'custom';

		const payload = {
			...form,
			custom_type: isCustomType ? form.custom_type || null : null,
			date: dayjs(form.date).format('YYYY-MM-DD'),
			room_id: (form.room_id || []).map((r) => Number(r.id)),
		};
		if (!payload.student_id) delete payload.student_id;
		onSubmit(payload);
	};

	const roomOptions = useMemo(
		() => normalizedRooms.map((r) => ({ value: Number(r.id), label: r.name })),
		[normalizedRooms]
	);

	const roomValue = [
		...(normalizedRooms.length > 0 && (form.room_id?.length || 0) === normalizedRooms.length
			? [{ value: 'all', label: 'Select All' }]
			: []),
		...normalizedRooms
			.filter((r) => (form.room_id || []).some((s) => Number(s.id) === Number(r.id)))
			.map((r) => ({ value: Number(r.id), label: r.name })),
	];

	return (
		<div>
			<h2 className="pb-64 text-20 font-bold">Event information</h2>

			<form onSubmit={handleSubmit}>
				<fieldset className="flex flex-col md:gap-16 pb-32 gap-y-48 md:gap-y-52">
					<div className="flex flex-col sm:flex-row items-end gap-16 gap-y-48 md:gap-y-52">
						<StyledInput
							type="date"
							label="Date"
							name="date"
							required
							onChange={(valOrEvent) =>
								handleChange(valOrEvent?.target ? valOrEvent : { name: 'date', value: valOrEvent })
							}
							error={!!errTxts.date?.length}
							helperText="Date must be on or after today"
							value={form.date ?? null}
							fullWidth
						/>

						<StyledSelect
							error={!!errTxts.type?.length}
							helperText={errTxts.type}
							label="Event Type"
							onChange={(e) => handleChange({ target: { name: 'type', value: e.target.value } })}
							fullWidth
							required
							options={types.map((t) => ({ value: t.id, label: t.type }))}
							placeholder="Select event type"
							value={form.type || ''}
						/>
					</div>

					{types.find((t) => t.id === form.type)?.type?.toLowerCase() === 'custom' ? (
						<StyledInput
							type="text"
							label="Custom Event Title"
							required
							name="custom_type"
							onChange={handleChange}
							error={!!errTxts.custom_type?.length}
							helperText={errTxts.custom_type}
							value={form.custom_type || ''}
							fullWidth
						/>
					) : null}

					<div className="flex flex-col sm:flex-row items-end gap-16 gap-y-40">
						<StyledSearchSelect
							label="Room Name"
							multi
							hasSelectAll
							resultsLoading={studentsLoading}
							selectAllText="Select All"
							onSelectAll={handleSelectAll}
							isAllSelected={isAllSelected}
							isIndeterminate={isIndeterminate}
							required
							error={!!errTxts.room_id?.length}
							helperText={errTxts.room_id}
							onChange={(vals) => {
								const values = vals || [];
								const clickedAll = values.some((v) => v?.value === 'all');

								if (clickedAll) {
									const toAll = (form.room_id?.length || 0) !== normalizedRooms.length;
									setForm((prev) => ({ ...prev, room_id: toAll ? normalizedRooms : [] }));
									return;
								}

								const selected = values
									.filter((v) => v?.value !== 'all')
									.map((v) => normalizedRooms.find((r) => Number(r.id) === Number(v.value)))
									.filter(Boolean);

								setForm((prev) => ({ ...prev, room_id: selected }));
							}}
							options={roomOptions}
							placeholder={normalizedRooms.length ? 'Select rooms' : 'Loading...'}
							value={roomValue}
							widthClass="w-full"
						/>

						<div className="w-full relative">
							<button
								className="absolute right-0 top-0"
								type="button"
								onClick={() => handleChange({ target: { name: 'student_id', value: '' } })}
							>
								Clear
							</button>

							<StyledSearchSelect
								label="Student Name"
								onChange={(opt) => handleChange({ target: { name: 'student_id', value: opt?.value || '' } })}
								options={
									students.length && form.room_id?.length === 1
										? students.map((s) => ({ value: s.id, label: `${s.first_name} ${s.last_name}` }))
										: []
								}
								placeholder={
									!form.room_id
										? 'Please select a room'
										: form.room_id.length > 1
											? 'Select one room to choose a student'
											: students.length
												? 'Select student'
												: 'No student found'
								}
								disabled={!form.room_id || form.room_id.length > 1}
								resultsLoading={studentsLoading}
								value={
									students
										.filter((s) => s.id === form.student_id)
										.map((s) => ({ value: s.id, label: `${s.first_name} ${s.last_name}` }))?.[0] || null
								}
								widthClass="w-full"
							/>
						</div>
					</div>

					<StyledInput
						label="Event Specification"
						name="description"
						onChange={handleChange}
						error={!!errTxts.description?.length}
						helperText={errTxts.description}
						placeholder="Enter details"
						multiline
						minRows={4}
						value={form.description || ''}
						fullWidth
					/>
				</fieldset>

				<div className="flex justify-center sm:justify-end">
					<div className="flex flex-col sm:flex-row justify-end sm:max-w-[200px] mt-16 w-full gap-5">
						{isSubmitting ? (
							<div className="flex align-center justify-center">
								<CircularProgress size={35} />
							</div>
						) : (
							<>
								<GradientButton onClick={onCancel} variant="light-blue" width="100%">
									Cancel
								</GradientButton>
								<GradientButton type="submit" variant="blurple" width="100%">
									{submitLabel}
								</GradientButton>
							</>
						)}
					</div>
				</div>
			</form>
		</div>
	);
};

export default EventForm;
